import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import customAPI from "./customAPI"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"

export const useGet = ({ url, queryKey }) => {
  const { token } = useSelector((state) => state.auth)

  const { isLoading, data, isError } = useQuery({
    queryKey: [queryKey],
    queryFn: async () => {
      const { data } = await customAPI.get(url, {
        headers: {
          Authorization: "Bearer " + token,
        },
      })
      return data.data
    },
  })
  return { isLoading, isError, data }
}

export const useEdit = ({
  queryKey,
  url,
  resetValues = () => {},
  successMessage = "",
  onSuccessTasks = () => {},
}) => {
  const queryClient = useQueryClient()
  const { token } = useSelector((state) => state.auth)

  const { mutate, isPending, isError } = useMutation({
    mutationFn: ({ id = "", data }) =>
      customAPI.patch(`${url}${id}`, data, {
        headers: {
          Authorization: "Bearer " + token,
        },
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [queryKey] })
      if (successMessage) {
        toast.success(successMessage)
      }
      resetValues()
      onSuccessTasks(data)
    },
    onError: (error) => {
      toast.error(error.response.data.message)
    },
  })
  return { mutate, isPending, isError }
}

export const useDelete = ({ queryKey, url, onSuccessTasks = () => {} }) => {
  const queryClient = useQueryClient()
  const { token } = useSelector((state) => state.auth)

  const { mutate, isLoading } = useMutation({
    mutationFn: ({ data = {} }) =>
      customAPI.delete(`${url}`, {
        data,
        headers: {
          Authorization: "Bearer " + token,
        },
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [queryKey] })
      onSuccessTasks(data)
    },
    onError: (error) => {
      toast.error(error.response.data.message)
    },
  })
  return { mutate, isLoading }
}

export const usePost = ({
  queryKey,
  url,
  resetValues = () => {},
  successMessage = "",
  onSuccessTasks = () => {},
}) => {
  const { token } = useSelector((state) => state.auth)

  const queryClient = useQueryClient()
  const { mutate, isPending, isError, data, isSuccess } = useMutation({
    mutationFn: async ({ data: newData }) => {
      const { data } = await customAPI.post(url, newData, {
        headers: {
          Authorization: "Bearer " + token,
        },
      })
      return data.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [queryKey] })
      if (successMessage) {
        toast.success(successMessage)
      }
      resetValues()
      onSuccessTasks(data)
    },
    onError: (error) => {
      toast.error(error.response.data.message, {
        position: "top-center",
      })
    },
  })
  return { isPending, mutate, isError, data, isSuccess }
}

export const useFetchWithBody = ({
  url,
  queryKey,
  inputData,
  manipulateData = false,
  manipulateDataFn = () => {},
}) => {
  const { token } = useSelector((state) => state.auth)
  const { isLoading, data, isError, isFetching } = useQuery({
    queryKey: [queryKey],
    queryFn: async () => {
      const { data } = await customAPI.post(url, inputData, {
        headers: {
          Authorization: "Bearer " + token,
        },
      })
      let newData = data.data
      if (manipulateData) {
        newData = manipulateDataFn(newData)
      }
      return newData
    },
  })
  return { isLoading, isError, data, isFetching }
}
